import { useTranslation } from '../../../i18n/useTranslation';
import { Badge } from '../../atoms/Badge/Badge';
import { Card } from '../../atoms/Card/Card';
import { LiveRegion } from '../../atoms/LiveRegion/LiveRegion';
import { Stack } from '../../atoms/Stack/Stack';
import { Text } from '../../atoms/Typography/Text';
import type { LivestockOverviewStatus } from '../../../services/livestockOverviewService';
import './LivestockOverview.css';

export interface OverviewProgressProps {
  status: Extract<LivestockOverviewStatus, 'detecting' | 'identifying'>;
}

type StepState = 'done' | 'active' | 'pending';

/**
 * The two phases of the background run as an ordered list; each step says its state in
 * text (never colour only). The current phase is announced politely.
 */
export function OverviewProgress({ status }: OverviewProgressProps) {
  const { t } = useTranslation();
  const steps: Array<{ key: 'detect' | 'identify'; state: StepState }> = [
    { key: 'detect', state: status === 'detecting' ? 'active' : 'done' },
    { key: 'identify', state: status === 'identifying' ? 'active' : 'pending' },
  ];
  const tone: Record<StepState, 'success' | 'info' | 'neutral'> = { done: 'success', active: 'info', pending: 'neutral' };

  return (
    <Card padding="lg" data-testid="overview-progress">
      <Stack gap="4">
        <LiveRegion busy>
          <Text weight="semibold">{t(`livestockOverview.progress.${status}`)}</Text>
        </LiveRegion>
        <ol className="livestock-overview__steps" aria-label={t('livestockOverview.progress.stepsLabel')}>
          {steps.map((step, index) => (
            <li key={step.key} className="livestock-overview__step" aria-current={step.state === 'active' ? 'step' : undefined}>
              <Text as="span">{`${index + 1}. ${t(`livestockOverview.progress.steps.${step.key}`)}`}</Text>
              <Badge tone={tone[step.state]}>{t(`livestockOverview.progress.state.${step.state}`)}</Badge>
            </li>
          ))}
        </ol>
        <Text size="sm" color="muted">{t('livestockOverview.progress.leaveHint')}</Text>
      </Stack>
    </Card>
  );
}

export default OverviewProgress;
